/**
 * financeSlice — state for the Finance feature (Figma 12.1).
 *
 * Holds the KPI stats + the payment-ledger rows, hydrated from the finance API
 * via the SSR reads + the Finance{Payments,Stats}Initializers (no client GET on
 * mount). Pure reducers.
 */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  FinanceInvoice,
  FinanceStat,
  LedgerRow,
  PaymentHistoryRow,
  PaymentsPage,
} from "@/components/finance/types";

export interface FinanceState {
  /** The 4 KPI stat cards (empty until hydrated). */
  stats: FinanceStat[];
  /** True until the KPI stats hydrate (they stream separately from payments). */
  statsLoading: boolean;
  /** Payment-ledger rows for the current page (empty until hydrated). */
  payments: LedgerRow[];
  /** Server pagination meta for the ledger. */
  paymentsCount: number;
  paymentsTotalPages: number;
  paymentsPage: number;
  /** True while a server-side ledger filter/search/page re-fetch is in flight. */
  paymentsLoading: boolean;
  /** The invoice shown on the detail screen (null until hydrated). */
  detailInvoice: FinanceInvoice | null;
  /** The customer's payment history on the detail screen. */
  detailHistory: PaymentHistoryRow[];
}

const initialState: FinanceState = {
  stats: [],
  // Stats + payments stream in separate Suspense boundaries — start in the
  // loading state so the cards / ledger show a skeleton until each hydrates.
  statsLoading: true,
  payments: [],
  paymentsCount: 0,
  paymentsTotalPages: 1,
  paymentsPage: 1,
  paymentsLoading: true,
  detailInvoice: null,
  detailHistory: [],
};

const financeSlice = createSlice({
  name: "finance",
  initialState,
  reducers: {
    /** Replace the KPI stats (SSR hydration / refetch). */
    setFinanceStats(state, action: PayloadAction<FinanceStat[]>) {
      state.stats = action.payload;
    },
    /** Replace the ledger page + pagination meta (SSR hydration / refetch). */
    setPayments(state, action: PayloadAction<PaymentsPage>) {
      state.payments = action.payload.rows;
      state.paymentsCount = action.payload.count;
      state.paymentsTotalPages = action.payload.totalPages;
      state.paymentsPage = action.payload.currentPage;
    },
    /** Toggle the in-flight flag for a ledger filter/search re-fetch. */
    setPaymentsLoading(state, action: PayloadAction<boolean>) {
      state.paymentsLoading = action.payload;
    },
    /** Toggle the KPI stats loading flag (SSR hydration / refetch). */
    setStatsLoading(state, action: PayloadAction<boolean>) {
      state.statsLoading = action.payload;
    },
    /** Replace the detail-screen invoice + payment history (SSR hydration). */
    setInvoiceDetail(
      state,
      action: PayloadAction<{
        invoice: FinanceInvoice;
        history: PaymentHistoryRow[];
      }>,
    ) {
      state.detailInvoice = action.payload.invoice;
      state.detailHistory = action.payload.history;
    },
  },
});

export const {
  setFinanceStats,
  setPayments,
  setPaymentsLoading,
  setStatsLoading,
  setInvoiceDetail,
} = financeSlice.actions;

export default financeSlice.reducer;
